import { useJobillaOrgId } from '@/hooks'
import { useAdDesignInvalidation } from '@/managers'
import {
	emitUnhandledError,
	formatFileValidationError,
	logger,
	type MediaAsset,
	type OrgAssets,
	useAlert,
	validateImageFile,
} from '@/ui'
import { DesignSimulator } from '@/ui'
import {
	type AdDesignStoreRequest,
	type CreateMediaDataCollectionEnumKey,
	mediaImageIndexQueryKey,
	roleEnum,
	useAdDesignShowDefaults,
	useAdDesignStore,
	useAdDesignUpdateDefaults,
	useBrandingsIndex,
	useCustomFontIndex,
	useMediaImageIndex,
	useMediaImageUpload,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback } from 'react'
import { RequireAccess } from '../../../../../components'

export const Route = createFileRoute('/_auth/company/$companyId/settings/design-simulator')({
	component: RouteComponent,
})

function RouteComponent() {
	return (
		<RequireAccess
			role={roleEnum.jobillan}
			fallback={<p className="text-sm text-gray-500 p-8">You do not have permission to access this page.</p>}
		>
			<RouteContent />
		</RequireAccess>
	)
}

function RouteContent() {
	const queryClient = useQueryClient()
	const jobillaOrgId = useJobillaOrgId()
	const alert = useAlert()

	const assets = useMediaImageIndex()
	const organisationId = jobillaOrgId
	const { data: customFontsData, isLoading: customFontsLoading } = useCustomFontIndex(organisationId)

	const orgAssets: OrgAssets = assets.data
		? {
			media: assets.data.map((asset) => ({
				id: asset.id,
				url: asset.url,
				category: 'image' as const,
				name: asset.fileName,
				width: asset.width,
				height: asset.height,
				fileName: asset.fileName,
				humanReadableSize: asset.humanReadableSize,
				createdAt: new Date(asset.createdAt),
				collection: asset.collectionName as MediaAsset['collection'],
				isReady: true,
			})),
		}
		: { media: [] }

	const imageStoreMutation = useMediaImageUpload({
		mutationKey: ['media-image-upload'],
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: mediaImageIndexQueryKey(),
			})
		},
		onError: (error) => {
			const data = error.response?.data
			const description = (data && 'message' in data ? data.message : data?.error) ?? error.message
			logger.error(error, {
				tags: { component: 'DesignSimulatorRoute', action: 'upload-media-image' },
			})
			emitUnhandledError({
				title: 'Upload failed',
				description,
				cause: error,
			})
		},
	})

	const handleUpload = (files: FileList, collection?: string) => {
		if (!organisationId) { return }

		const file = files[0]
		if (!file) { return }

		const validationError = validateImageFile(file)
		if (validationError) {
			alert.open(
				validationError.type === 'file-too-large' ? 'Image too large' : 'Invalid file type',
				formatFileValidationError(validationError),
				'OK',
				() => {},
			)
			return
		}

		imageStoreMutation.mutate({
			organisation: jobillaOrgId,
			data: { media: file, collection: (collection ?? 'uploaded') as CreateMediaDataCollectionEnumKey },
		})
	}

	// Gallery integration hooks
	const createDesign = useAdDesignStore()
	const { data: defaults } = useAdDesignShowDefaults(organisationId)
	const updateDefaults = useAdDesignUpdateDefaults()
	const thumbnailUpload = useMediaImageUpload()
	const invalidateDesigns = useAdDesignInvalidation()
	const { data: brands } = useBrandingsIndex({
		'sort': ['created_at'],
		'filter[organisation_id]': organisationId,
	}, {
		enabled: !!organisationId,
	})

	const handleCreateDesign = useCallback(
		async (data: AdDesignStoreRequest) => {
			if (!organisationId) { throw new Error('No organisation ID') }
			return createDesign.mutateAsync({
				organisation: organisationId,
				data,
			})
		},
		[organisationId, createDesign],
	)

	const handleUpdateDefaults = useCallback(
		async (data: { defaultFeedAdDesignId: string | null; defaultStoryAdDesignId: string | null }) => {
			if (!organisationId) { return }
			await updateDefaults.mutateAsync({
				organisation: organisationId,
				data,
			})
		},
		[organisationId, updateDefaults],
	)

	const handleUploadThumbnail = useCallback(async (file: File) => {
		if (!organisationId) { throw new Error('No organisation ID') }
		return thumbnailUpload.mutateAsync({
			organisation: organisationId,
			data: { media: file, collection: 'design' as never },
		})
	}, [organisationId, thumbnailUpload])

	const handleInvalidateDesigns = useCallback(() => {
		invalidateDesigns(organisationId)
	}, [organisationId, invalidateDesigns])

	return (
		<DesignSimulator
			orgAssets={orgAssets}
			onAssetsUpload={handleUpload}
			customFonts={customFontsData}
			customFontsLoading={customFontsLoading}
			brands={brands}
			defaults={defaults}
			onCreateDesign={handleCreateDesign}
			onUpdateDefaults={handleUpdateDefaults}
			onUploadThumbnail={handleUploadThumbnail}
			onInvalidateDesigns={handleInvalidateDesigns}
		/>
	)
}
